import { useEffect, useRef, useState } from 'react'
import { getAsync, runInBackground } from '../../api-new/service/api.service'

// Visibility-aware, sentinel-gated live refresh for an AG Grid infinite table.
// Polls a cheap `range/sentinel` endpoint while the tab is visible and only
// calls refreshInfiniteCache() when the sentinel value changes. Opt-in: does
// nothing unless pollInterval + sentinelEndpoint are provided.
export const useLiveRefresh = ({ gridRef, pollInterval, sentinelServer, sentinelEndpoint }) => {
  const [health, setHealth] = useState('live')
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const lastSentinel = useRef(undefined)

  useEffect(() => {
    if (!pollInterval || !sentinelEndpoint) return

    // A new endpoint (a page's filter changed the query on it) is a new
    // baseline: its sentinel is not comparable with the old one's. A read of
    // the old endpoint that lands after this effect ended is dropped.
    lastSentinel.current = undefined
    let live = true
    let interval = null

    const tick = async () => {
      try {
        const res = await runInBackground(() => getAsync(sentinelServer, sentinelEndpoint))
        if (!live) return
        const sentinel = res?.data?.sentinel ?? null
        if (lastSentinel.current === undefined) {
          lastSentinel.current = sentinel // baseline; don't refresh on first read
        } else if (sentinel !== lastSentinel.current) {
          lastSentinel.current = sentinel
          gridRef.current?.api?.refreshInfiniteCache()
          setLastUpdatedAt(new Date())
        }
        setHealth('live')
      } catch {
        if (live) setHealth('reconnecting')
      }
    }

    const start = () => { if (!interval) interval = setInterval(tick, pollInterval) }
    const stop = () => { if (interval) { clearInterval(interval); interval = null } }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') { tick(); start() } else { stop() }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    // This first tick() is the sentinel heartbeat establishing its baseline
    // (see "don't refresh on first read" above) -- not the table's first data
    // load, which happens separately via the grid's own datasource/getRows.
    // It's rightly marked background by runInBackground inside tick().
    if (document.visibilityState === 'visible') { tick(); start() }

    return () => { live = false; stop(); document.removeEventListener('visibilitychange', handleVisibility) }
  }, [pollInterval, sentinelServer, sentinelEndpoint, gridRef])

  return { health, lastUpdatedAt }
}
